import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ContextualAIChat from "@/components/ContextualAIChat";
import { 
  ArrowLeft, 
  Briefcase,
  Laptop,
  Users,
  TrendingUp,
  Clock,
  DollarSign,
  Lightbulb,
  ChevronRight,
  Rocket,
  GraduationCap,
  Handshake
} from "lucide-react";

const EarnPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    setUser(JSON.parse(storedUser));
  }, [navigate]);

  const earningStrategies = [
    {
      icon: Laptop,
      title: "Freelancing",
      description: "Use your skills to take on projects",
      potentialIncome: "$500 - $5,000+/mo",
      timeRequired: "10-40 hrs/week",
      difficulty: "Medium",
      examples: ["Web development", "Writing", "Design", "Consulting"]
    },
    {
      icon: TrendingUp,
      title: "Ask for a Raise",
      description: "Negotiate your current salary",
      potentialIncome: "5-20% increase",
      timeRequired: "Preparation time",
      difficulty: "Low",
      examples: ["Document achievements", "Research market rates", "Practice negotiation"]
    },
    {
      icon: GraduationCap,
      title: "Upskill",
      description: "Learn high-demand skills",
      potentialIncome: "Higher salary potential",
      timeRequired: "3-6 months",
      difficulty: "Medium",
      examples: ["Certifications", "Online courses", "Bootcamps"]
    },
    {
      icon: Users,
      title: "Side Business",
      description: "Start a small business on the side",
      potentialIncome: "$1,000 - $10,000+/mo",
      timeRequired: "15-30 hrs/week",
      difficulty: "High",
      examples: ["E-commerce", "Tutoring", "Services", "Content creation"]
    },
    {
      icon: Handshake,
      title: "Network",
      description: "Build professional connections",
      potentialIncome: "Opportunities",
      timeRequired: "Ongoing",
      difficulty: "Low",
      examples: ["LinkedIn", "Industry events", "Mentorship", "Referrals"]
    },
    {
      icon: Rocket,
      title: "Career Switch",
      description: "Move to higher-paying field",
      potentialIncome: "20-50% increase",
      timeRequired: "6-12 months",
      difficulty: "High",
      examples: ["Tech industry", "Healthcare", "Finance", "Sales"]
    }
  ];

  const quickWins = [
    { title: "Review subscriptions", savings: "$50-200/mo", icon: "💳" },
    { title: "Sell unused items", earnings: "$100-1,000", icon: "📦" },
    { title: "Cash back apps", savings: "$20-50/mo", icon: "💰" },
    { title: "Negotiate bills", savings: "$30-100/mo", icon: "📞" }
  ];

  const suggestedPrompts = [
    "What skills should I learn to earn more in my field?",
    "How do I negotiate a salary raise?",
    "What side hustles match my skills?",
    "How can I increase my income in 6 months?"
  ];

  const userSkills = user?.skills || [];
  const userProfession = user?.profession || "Not specified";

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="font-heading text-xl font-semibold">Earn More</h1>
            <p className="text-xs text-muted-foreground">Increase your income</p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Profile Summary */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-tamba-yellow/30 bg-gradient-to-br from-tamba-yellow/5 to-transparent">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl bg-tamba-yellow/20 flex items-center justify-center">
                  <Briefcase className="h-7 w-7 text-tamba-yellow" />
                </div>
                <div className="flex-1">
                  <h2 className="font-heading text-lg font-semibold mb-1">Your Profile</h2>
                  <p className="text-muted-foreground text-sm mb-2">
                    <span className="font-medium text-foreground">{userProfession}</span>
                    {user?.years_experience && ` • ${user.years_experience} years experience`}
                  </p>
                  {userSkills.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {userSkills.map((skill, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-tamba-yellow/10 text-tamba-yellow rounded-full">
                          {skill}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Quick Wins */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="font-heading text-lg font-semibold mb-4">Quick Wins</h2>
          <div className="grid grid-cols-2 gap-3">
            {quickWins.map((win, index) => (
              <Card key={index} className="border-border/50 hover:border-tamba-teal/30 transition-all">
                <CardContent className="p-4 text-center">
                  <span className="text-2xl mb-2 block">{win.icon}</span>
                  <h3 className="font-medium text-sm mb-1">{win.title}</h3>
                  <p className="text-xs text-tamba-teal font-medium">
                    {win.savings || win.earnings}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Earning Strategies */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="font-heading text-lg font-semibold mb-4">Earning Strategies</h2>
          <div className="space-y-4">
            {earningStrategies.map((strategy, index) => (
              <Card key={index} className="border-border/50 hover:border-tamba-yellow/30 hover:shadow-md transition-all">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-tamba-yellow/10 flex items-center justify-center flex-shrink-0">
                      <strategy.icon className="h-6 w-6 text-tamba-yellow" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold">{strategy.title}</h3>
                        <span className="text-xs px-2 py-1 rounded-full bg-secondary">
                          {strategy.difficulty}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{strategy.description}</p>
                      <div className="flex items-center gap-4 text-xs mb-2">
                        <span className="flex items-center gap-1">
                          <DollarSign className="h-3 w-3 text-tamba-teal" />
                          {strategy.potentialIncome}
                        </span>
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {strategy.timeRequired}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {strategy.examples.map((example, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 bg-secondary rounded">
                            {example}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </motion.div>

        {/* Income Growth Tips */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="border-tamba-blue/30 bg-gradient-to-br from-tamba-blue/5 to-transparent">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-tamba-blue" />
                Pro Tips
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-tamba-blue mt-0.5">•</span>
                  <span>Track your achievements and wins - you'll need them for negotiations</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-tamba-blue mt-0.5">•</span>
                  <span>Set aside 10% of any income increase for investing</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-tamba-blue mt-0.5">•</span>
                  <span>Focus on skills that are in high demand but low supply</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-tamba-blue mt-0.5">•</span>
                  <span>Your network is your net worth - invest time in relationships</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card className="border-tamba-coral/30 bg-gradient-to-br from-tamba-coral/5 to-transparent">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold mb-1">Get personalized earning advice</h3>
                  <p className="text-sm text-muted-foreground">
                    Based on your skills and experience
                  </p>
                </div>
                <Button 
                  onClick={() => navigate("/chat")}
                  className="rounded-full bg-tamba-coral hover:bg-tamba-coral/90"
                >
                  Ask AI
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Contextual AI Chat */}
      {user && (
        <ContextualAIChat
          context="earn"
          userId={user.id}
          pageData={{ 
            profession: user.profession, 
            skills: user.skills,
            years_experience: user.years_experience 
          }}
          suggestedPrompts={suggestedPrompts}
          accentColor="tamba-yellow"
        />
      )}
    </div>
  );
};

export default EarnPage;
